/*
 * File: c:\Users\j.mazat\Desktop\Autobuy\AutobuyFrontOffice\AutobuyClient\src\Content\Password\PasswordInput\PasswordInput.tsx
 * Project: c:\Users\j.mazat\Desktop\Autobuy\AutobuyFrontOffice\AutobuyClient
 * Created Date: Tuesday September 19th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Monday, 2nd October 2023 11:14:21
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import { useLang } from "Router/LangueProvider";
import { forwardRef, useImperativeHandle, useEffect, useState, useRef } from "react";
import { Form } from "react-bootstrap";
import Styles from "Content/Password/PasswordConfirm/PasswordConfirm.module.css";
import PasswordRulesCheck from "../PasswordRulesCheck/PasswordRulesCheck";
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import ButtonCustom from "Theme/Component/ButtonCustom";

type PasswordInputProps = {
    HandleSubmit: Function,
    askCurrentPwd: boolean,
}

const PasswordInput = forwardRef((props: PasswordInputProps, ref) => {
    const {translator} = useLang();
    const [isPwdChecked, setIsPwdChecked] = useState<boolean | null>(null);
    const [pairMatch, setPairMatch] = useState<boolean| null>(null);
    const [isSensable, setIsSendable] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [passwordConfirm, setPasswordConfirm] = useState<string>("");
    const regexChecked = useRef<{length: boolean | null, min: boolean | null, maj: boolean | null, nb: boolean| null, spec: boolean| null}>({length: null, min: null, maj: null, nb: null, spec: null});
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showCurPassword, setShowCurPassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState<string>("");

    const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);

    const handleMouseDownNewPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };
    const handleClickShowCurPassword = () => setShowCurPassword((show) => !show);

    const handleMouseDownCurPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
    };

    useImperativeHandle(ref, () => {
        if (Object.values(regexChecked.current).every((val) => val === true))
            return password;
    }, [isPwdChecked]);

    useEffect(() => {
        if (Object.values(regexChecked.current).every((val) => val === true)) {
            if (password === passwordConfirm) {
                setIsSendable(true);
                setPairMatch(true);
            } else {
                setIsSendable(false);
                setPairMatch(false);
            }
        } else {
            setPairMatch(null);
            setIsSendable(false);
        }
    }, [password, passwordConfirm]);

    useEffect(() => {
        if (isPwdChecked)
            setIsPwdChecked(false);
    }, [isPwdChecked]);

    const onBlurFirst = (val?: string) =>  {
        const RegexMin = /[a-z]+/;
        const RegexMaj = /[A-Z]+/;
        const RegexNumber = /[\d]+/;
        const RegexSpec = /[^\w"'`;\s]|_/;
        const toCheck = val ?? password;

        if (toCheck.length > 0) {
            if (toCheck.length >= 8)
                regexChecked.current.length = true;
            else
                regexChecked.current.length = false;

            if (RegexMin.test(toCheck))
                regexChecked.current.min = true;
            else
                regexChecked.current.min = false;

            if (RegexMaj.test(toCheck))
                regexChecked.current.maj = true;
            else
                regexChecked.current.maj = false;

            if (RegexNumber.test(toCheck))
                regexChecked.current.nb = true;
            else
                regexChecked.current.nb = false;

            if (RegexSpec.test(toCheck))
                regexChecked.current.spec = true;
            else
                regexChecked.current.spec = false;
        } else {
            regexChecked.current.length = null;
            regexChecked.current.min = null;
            regexChecked.current.maj = null;
            regexChecked.current.nb = null;
            regexChecked.current.spec = null;
        }
        setIsPwdChecked(true);
    };

    return (
        <Form onSubmit={(e) => props.HandleSubmit(e, currentPassword, password)} className={`${Styles.formStyle}`}>
            <div className={`${Styles.inputHolder}`}>
                <div className={`${Styles.flex}`}>
                    {
                        props.askCurrentPwd ?
                        <FormControl sx={{ m: 1, '& .Mui-focused': {color: "black !important"} }} variant="outlined" size="small">
                            <InputLabel htmlFor="outlined-adornment-password-current">{translator.decode("password.current")}</InputLabel>
                            <OutlinedInput
                                sx={{'& .MuiOutlinedInput-notchedOutline': {borderColor: "grey !important"}}}
                                id="outlined-adornment-password-current"
                                value={currentPassword}
                                className={`text-start ${Styles.passwordInput}`}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                type={showCurPassword ? 'text' : 'password'}
                                endAdornment={ <InputAdornment position="end">
                                                <IconButton aria-label="toggle password visibility" onClick={handleClickShowCurPassword} onMouseDown={handleMouseDownCurPassword} edge="end" >
                                                    {showCurPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                                                </IconButton>
                                            </InputAdornment> }
                                label={translator.decode("password.current")}
                                required
                            />
                        </FormControl>
                        :null
                    }
                    <FormControl sx={{ m: 1 ,'& .Mui-focused': {color: "black !important"}}} variant="outlined" size="small">
                        <InputLabel htmlFor="outlined-adornment-password">{translator.decode("password.newPassword")}</InputLabel>
                        <OutlinedInput
                            sx={{'& .MuiOutlinedInput-notchedOutline': {borderColor: pairMatch !== null ? (pairMatch ? "green !important" : "grey !important") : "grey !important"}, textAlign: "start"}}
                            id="outlined-adornment-password"
                            className={`text-start ${Styles.passwordInput}`}
                            value={password}
                            onChange={(e) => { if (e.target.value.length === 0) onBlurFirst(e.target.value); setPassword(e.target.value)}}
                            onBlur={() => onBlurFirst()}
                            type={showNewPassword ? 'text' : 'password'}
                            endAdornment={ <InputAdornment position="end">
                                            <IconButton aria-label="toggle password visibility" onClick={handleClickShowNewPassword} onMouseDown={handleMouseDownNewPassword} edge="end" >
                                                {showNewPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                                            </IconButton>
                                        </InputAdornment> }
                            label={translator.decode("password.newPassword")}
                            required
                        />
                    </FormControl>
                    <FormControl sx={{ m: 1 ,'& .Mui-focused': {color: "black !important"}}} variant="outlined" size="small">
                        <InputLabel htmlFor="outlined-adornment-password-confirmation">{translator.decode("login.placeholder.passwordConfirm")}</InputLabel>
                        <OutlinedInput
                            sx={{'& .MuiOutlinedInput-notchedOutline': {borderColor: pairMatch !== null ? (pairMatch ? "green !important" : "red !important") : "grey !important"}}}
                            id="outlined-adornment-password-confirmation"
                            className={`text-start ${Styles.passwordInput}`}
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value)}
                            type={showNewPassword ? 'text' : 'password'}
                            endAdornment={ <InputAdornment position="end">
                                            <IconButton aria-label="toggle password visibility" onClick={handleClickShowNewPassword} onMouseDown={handleMouseDownNewPassword} edge="end" >
                                                {showNewPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                                            </IconButton>
                                        </InputAdornment>}
                            label={translator.decode("login.placeholder.passwordConfirm")}
                            required
                        />
                    </FormControl>
                </div>
                <PasswordRulesCheck regexChecked={regexChecked.current} />
            </div>
            <ButtonCustom title="Send" className={`${Styles.btnSend}`} disabled={!isSensable} type="submit">
                {
                    props.askCurrentPwd ?
                    translator.decode("dynamic.store"): translator.decode("dynamic.send")
                }
            </ButtonCustom>
        </Form>
    )
});

export default PasswordInput;
